import React, { useCallback, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import get from "lodash-es/get";
import PropTypes from "prop-types";

// Config
import { capturePages } from "../../config/values";
import { getRoute, urls } from "../../config/routes";

// Utils
import useInjuries from "../../hooks/useInjuries";
import usePhysicalDamage from "../../hooks/usePhysicalDamage";
import useQuery from "../../hooks/useQuery";

// Components
import ToolWrapper from "./ToolWrapper";
import LoadingSection from "../../components/LoadingSection/LoadingSection";

const ToolPage = ({ page }) => {
  const isInjury = page === capturePages.INJURY;
  const { caseId, injuryId, physicalDamageId, imgIndex } = useParams();
  const history = useHistory();
  const query = useQuery();

  // Both hooks fetch and hold Redux state, so both are always called (rules
  // of hooks) - `enabled` keeps the one that doesn't match `page` from
  // dispatching a fetch with an undefined id. Only the matching one's data
  // is actually used.
  const injury = useInjuries(caseId, injuryId, isInjury);
  const physicalDamage = usePhysicalDamage(
    caseId,
    physicalDamageId,
    !isInjury
  );
  const item = isInjury ? injury : physicalDamage;

  const images = get(item, `data.images`, []);
  const currentImage = images[imgIndex];

  const detailRoute = isInjury ? urls.injuryDetail : urls.physicalDamageDetail;
  const annotationToolRoute = isInjury
    ? urls.annotationTool
    : urls.physicalDamageAnnotationTool;

  const goBack = useCallback(() => {
    history.push(getRoute(detailRoute, { caseId, injuryId, physicalDamageId }));
  }, [caseId, detailRoute, history, injuryId, physicalDamageId]);

  useEffect(() => {
    if (query) {
      const filename = query.get("filename");
      images.forEach((image, i) => {
        if (image.filename === filename && i !== Number(imgIndex)) {
          history.push(
            getRoute(annotationToolRoute, {
              caseId,
              injuryId,
              physicalDamageId,
              index: i
            })
          );
        }
      });
    }
  }, [
    annotationToolRoute,
    caseId,
    history,
    images,
    imgIndex,
    injuryId,
    physicalDamageId,
    query
  ]);

  useEffect(() => {
    if (!currentImage && item.initialized) goBack();
  }, [currentImage, goBack, item.initialized]);

  return currentImage ? (
    <ToolWrapper page={page} goBack={goBack} currentImage={currentImage} />
  ) : (
    <LoadingSection />
  );
};

ToolPage.propTypes = {
  page: PropTypes.oneOf(Object.values(capturePages)).isRequired
};

export default ToolPage;
